import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Printer, Mail, Clock, ExternalLink, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const PHONE = "(484) 425-9712";
const FAX = "(484) 920-6029";
const EMAIL = "nina.latinomultiservice@gmail.com";
const ADDRESS = "1639 E Cheltenham Ave, Philadelphia, PA 19124";
const MAPS = "https://www.google.com/maps/search/?api=1&query=1639+E+Cheltenham+Ave+Philadelphia+PA+19124";

export const Footer = () => {
  const { t } = useLanguage();
  const f = t.footer;

  const hours = [
    { day: f.days.monThu, val: f.hoursValue },
    { day: f.days.fri, val: f.hoursValue },
    { day: f.days.sat, val: f.hoursValue },
    { day: f.days.sun, val: f.closed, closed: true },
  ];

  return (
    <footer id="footer" className="relative bg-[var(--brand-charcoal)] text-[var(--brand-offwhite)]">
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white">
                <img src="/assets/nina-mark.png" alt="Nina Latinos" className="h-9 w-9 object-contain" />
              </span>
              <div>
                <div className="font-heading text-xl font-bold leading-none">NINA</div>
                <div className="text-xs text-white/60">Latinos Multi Services, LLC</div>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/70">{f.about}</p>
            <p className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--brand-burgundy)]" /> {f.seHablaEspanol}
            </p>
          </div>

          {/* Location & Contact */}
          <div>
            <h4 className="font-heading text-lg font-semibold">{f.locationTitle}</h4>
            <a href={MAPS} target="_blank" rel="noreferrer" className="mt-3 flex items-start gap-2.5 text-sm text-white/75 transition-colors hover:text-white" data-testid="footer-address">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[var(--brand-burgundy)]" />
              <span>{ADDRESS}</span>
            </a>
            <a href={MAPS} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--brand-burgundy-on-dark)] transition-colors hover:text-white">
              {f.getDirections} <ExternalLink className="h-3 w-3" />
            </a>

            <h4 className="mt-6 font-heading text-lg font-semibold">{f.contactTitle}</h4>
            <ul className="mt-3 space-y-2 text-sm text-white/75">
              <li><a href={`tel:+14844259712`} className="flex items-center gap-2.5 transition-colors hover:text-white" data-testid="footer-phone"><Phone className="h-4 w-4 text-[var(--brand-burgundy)]" />{f.office}: {PHONE}</a></li>
              <li className="flex items-center gap-2.5"><Printer className="h-4 w-4 text-[var(--brand-burgundy)]" />{f.fax}: {FAX}</li>
              <li><a href={`mailto:${EMAIL}`} className="flex items-center gap-2.5 break-all transition-colors hover:text-white" data-testid="footer-email"><Mail className="h-4 w-4 shrink-0 text-[var(--brand-burgundy)]" />{EMAIL}</a></li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="flex items-center gap-2 font-heading text-lg font-semibold"><Clock className="h-5 w-5 text-[var(--brand-burgundy)]" />{f.hoursTitle}</h4>
            <div className="mt-3 overflow-hidden rounded-xl border border-white/10" data-testid="footer-hours">
              {hours.map((h, i) => (
                <div key={h.day} className={`flex items-center justify-between px-3.5 py-2.5 text-sm ${i % 2 === 0 ? "bg-white/5" : ""}`}>
                  <span className="text-white/75">{h.day}</span>
                  <span className={`font-semibold ${h.closed ? "text-[var(--brand-burgundy-on-dark)]" : "text-white"}`}>{h.val}</span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-white/50">{f.holidayNote}</p>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-heading text-lg font-semibold">{f.quickLinks}</h4>
            <ul className="mt-3 space-y-2 text-sm text-white/75">
              <li><a href="#services" className="transition-colors hover:text-white">{t.nav.services}</a></li>
              <li><a href="#about" className="transition-colors hover:text-white">{t.nav.about}</a></li>
              <li><a href="#portal" className="transition-colors hover:text-white">{t.nav.portal}</a></li>
              <li><Link to="/admin/login" className="inline-flex items-center gap-1.5 transition-colors hover:text-white" data-testid="footer-admin-link"><ShieldCheck className="h-4 w-4 text-[var(--brand-burgundy)]" />{f.adminLink}</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-2 border-t border-white/10 pt-6 text-center text-xs text-white/50 sm:flex-row sm:text-left">
          <p>© {new Date().getFullYear()} Nina Latinos Multi Services, LLC. {f.rights}</p>
          <p className="font-medium text-white/60">{f.tagline}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
